<script lang="ts">
  import { onMount } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { openOverlay, closeOverlayById } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { mdiDelete, mdiDrawPen, mdiPencil } from "@mdi/js"
  import LetterSignatureOverlay from "./_modules/letter-signature-overlay"
  import { buildMobileSignatureDeepLink, consumeSignatureAction } from "./_logic/signatureDeepLink"

  let {
    organizationId,
    signature = $bindable(),
    disabled,
  }: {
    organizationId: string
    signature: OrganizationLetterSignature | null
    disabled: boolean
  } = $props()
  let active = true
  let overlayId: string | null = null

  const openSignature = () => {
    if (disabled || overlayId) return

    const accept = (value: OrganizationLetterSignature) => {
      if (active && !disabled) signature = value
    }
    overlayId = openOverlay({
      title: signature ? "Unterschrift ersetzen" : "Unterschrift setzen",
      content: LetterSignatureOverlay,
      isDark: true,
      defaultMobileFullscreen: true,
      properties: {
        initialSignature: signature,
        mobileSigningUrl: buildMobileSignatureDeepLink(window.location.origin, organizationId),
        organizationId,
        onSave: accept,
        onPhotoUploaded: accept,
      },
      onClose: () => {
        overlayId = null
      },
    })
  }

  onMount(() => {
    const action = consumeSignatureAction(window.location.href)

    if (action.consumed) {
      window.history.replaceState(window.history.state, "", action.cleanedUrl)

      if (action.shouldOpen && !disabled) openSignature()
    }

    return () => {
      active = false

      if (overlayId) closeOverlayById(overlayId)
    }
  })
</script>

<section aria-label="Unterschrift" class="signature-section">
  <SectionTitle
    title="Unterschrift"
    brightBackground={true}
    small={true}
    helpModalTextId="planningLetterSignature"
  />
  {#if signature}
    <img
      src={`data:image/png;base64,${signature.image_png_base64}`}
      alt="Unterschrift im Entwurf"
    />
  {:else}
    <p
      >Keine Unterschrift hinterlegt. Briefe enthalten weiterhin den Hinweis zur automatischen
      Erstellung.</p
    >
  {/if}
  <p
    >Die Unterschrift gilt für Ankündigungs- und Verschiebungsbriefe sowie Aushänge. Änderungen
    werden erst mit „Speichern“ übernommen.</p
  >
  <div class="actions">
    <CTA
      type="secondaryButton"
      content={signature ? "Unterschrift ersetzen" : "Unterschrift setzen"}
      icon={signature ? mdiPencil : mdiDrawPen}
      onClick={openSignature}
      {disabled}
      small={true}
      options={{
        label: "Unterschriftoptionen",
        disabled,
        items: signature
          ? [
              {
                id: "remove-signature",
                label: "Unterschrift entfernen",
                icon: mdiDelete,
                tone: "danger",
                onClick: () => {
                  if (!disabled) signature = null
                },
              },
            ]
          : [],
      }}
    />
  </div>
</section>

<style>
  .signature-section {
    display: grid;
    gap: 0.75rem;
    min-width: 0;
  }
  img {
    width: 10rem;
    height: 3rem;
    object-fit: contain;
    object-position: left;
    background: white;
  }
  p {
    margin: 0;
    line-height: 1.5;
    color: var(--text-invers-100);
  }
  .actions {
    display: flex;
    gap: 0.65rem;
    flex-wrap: wrap;
  }
</style>
